<template>
  <div class="row">
    <div class="col-lg-6">
      <h4><i class="bi bi-lightning text-warning me-1"></i>Fastest Flips</h4>
      <div class="mb-4">
        <div v-if="loading" class="d-flex justify-content-center py-4">
          <loading-spinner />
        </div>
        <div v-else-if="fastestList.length > 0"  class="card bg-light-subtle shadow">
          <div class="card-body p-0">
            <div
                v-for="(item, idx) in fastestList"
                :key="item.order_id"
                class="speed-row d-flex align-items-center px-3 py-2"
                :class="{ 'border-top': idx > 0, 'bg-success-subtle': idx === 0 }"
                role="button"
                @click="openModal(item)"
            >
              <lazy-image
                  :image-src="item.thumbnail || '/images/logo.svg'"
                  class="speed-thumb me-3 flex-shrink-0"
                  :title="item.title"
              />
              <div class="flex-grow-1 overflow-hidden">
                <div class="text-truncate small fw-semibold">{{ item.title || item.sold_remote_id }}</div>
                <div class="text-muted" style="font-size:0.75rem;">
                  {{ formatDate(item.listing_date) }} <i class="bi bi-arrow-right mx-1"></i> {{ formatDate(item.sold_date) }}
                </div>
              </div>
              <div class="text-end ms-3 flex-shrink-0">
                <div class="small fw-bold">{{ formatTimeToSell(item) }}</div>
                <div class="text-muted" style="font-size:0.75rem;">${{ parseFloat(item.sale_price).toFixed(2) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted small py-3">No data for this period.</div>
      </div>
    </div>

    <div class="col-lg-6">
      <h4><i class="bi bi-hourglass-split text-info me-1"></i>Slowest Flips</h4>
      <div class="mb-4">
        <div v-if="loading" class="d-flex justify-content-center py-4">
          <loading-spinner />
        </div>
        <div v-else-if="slowestList.length > 0" class="card bg-light-subtle shadow">
          <div class="card-body p-0">
            <div
                v-for="(item, idx) in slowestList"
                :key="item.order_id"
                class="speed-row d-flex align-items-center px-3 py-2"
                :class="{ 'border-top': idx > 0, 'bg-danger-subtle': idx + 1 === slowestList.length }"
                role="button"
                @click="openModal(item)"
            >
              <lazy-image
                  :image-src="item.thumbnail || '/images/logo.svg'"
                  class="speed-thumb me-3 flex-shrink-0"
                  :title="item.title"
              />
              <div class="flex-grow-1 overflow-hidden">
                <div class="text-truncate small fw-semibold">{{ item.title || item.sold_remote_id }}</div>
                <div class="text-muted" style="font-size:0.75rem;">
                  {{ formatDate(item.listing_date) }} <i class="bi bi-arrow-right mx-1"></i> {{ formatDate(item.sold_date) }}
                </div>
              </div>
              <div class="text-end ms-3 flex-shrink-0">
                <div class="small fw-bold">{{ formatTimeToSell(item) }}</div>
                <div class="text-muted" style="font-size:0.75rem;">${{ parseFloat(item.sale_price).toFixed(2) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted small py-3">No data for this period.</div>
      </div>
    </div>

    <inventory-detail-modal
      v-if="selectedItem"
      v-model="showDetailModal"
      :item="selectedItem"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import LazyImage from '@/app/components/Utils/LazyImage.vue'
import InventoryDetailModal from '@/app/components/inventory/InventoryDetailModal.vue'
import { DateUtil, flipTimeBreakdown } from '@/services/dateUtils'

const props = defineProps({
  range: { type: Object, required: true }
})

const store = useStore()
const loading = ref(false)
const items = ref([])
const showDetailModal = ref(false)
const selectedItem = ref(null)

const storeConfig = computed(() => store.state.eshop.eshop)
const dateUtil = computed(() => new DateUtil(storeConfig.value?.timezone || store.state.defaultTimezone))

const fastestList = computed(() => {
   const filtered = items.value.filter(i => i.flip_type === 'fastest')
   return filtered
 })

const slowestList = computed(() => {
  const filtered = items.value.filter(i => i.flip_type === 'slowest')
  return [...filtered].sort((a, b) => a.minutes_to_sell - b.minutes_to_sell)
})

function formatTimeToSell(item) {
  const { value, unit } = flipTimeBreakdown(item.minutes_to_sell)
  return `${value} ${unit === 'hrs' ? 'hours' : unit}`
}

function formatDate(val) {
  if (!val) return '—'
  return new Date(val).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function openModal(item) {
  selectedItem.value = { remote_id: item.sold_remote_id, title: item.title }
  showDetailModal.value = true
}

async function fetchData() {
  loading.value = true
  try {
    const from = dateUtil.value.toISODate(props.range.from)
    const to = dateUtil.value.toISODate(props.range.to)
    const response = await store.state.api.get('/inventory/items-sale-velocity', { params: { from, to } })
    items.value = response.data?.data || []
  } catch (e) {
    console.error('ItemsSaleSpeed fetch error:', e)
    items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(fetchData)
watch(() => props.range, fetchData, { deep: true })
</script>

<style scoped>
.speed-rank {
  min-width: 1.5rem;
  font-variant-numeric: tabular-nums;
}
.speed-row:hover {
  background-color: #eee;
}
.speed-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
